<template>
  <component
    :is="component"
    v-if="component"
    class="sf-icon"
    :size="size"
    :stroke-width="strokeWidth"
    aria-hidden="true"
  />
</template>

<script>
// Thin wrapper around lucide-vue-next so the rest of the codebase can keep
// referring to icons by short, semantic names. Mapping below is the only
// place that knows about Lucide internals.
import {
  ArrowLeftRight,
  ArrowUpRight,
  ArrowUpFromLine,
  AtSign,
  Bell,
  Bold,
  Box,
  Calendar,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Code,
  Cloud,
  Compass,
  Computer,
  Copy,
  Diamond,
  FileText,
  Folder,
  FolderOpen,
  Gem,
  Globe,
  HardDrive,
  Heading1,
  Heading2,
  Heading3,
  Highlighter,
  Italic,
  Laptop,
  List,
  ListOrdered,
  Image as ImageIcon,
  Instagram,
  Languages,
  LifeBuoy,
  Loader2,
  Lock,
  Lightbulb,
  LineSquiggle,
  Eye,
  EyeOff,
  PencilLine,
  RotateCcw,
  LogOut,
  Mail,
  Monitor,
  Moon,
  MoreHorizontal,
  Music,
  Palette,
  Pencil,
  Printer,
  Quote,
  Redo2,
  Baseline,
  Share,
  TextQuote,
  UserRoundPlus,
  Save,
  Scale,
  ScrollText,
  Search,
  Settings2,
  Share2,
  Shield,
  Sigma,
  Smartphone,
  Sparkles,
  Star,
  Strikethrough,
  Sun,
  Tablet,
  Trash2,
  Underline,
  Undo2,
  UserPlus,
  Users,
  Volume2,
  X,
  Zap,
  Atom,
  BookOpen,
  BookText,
  Brain,
  Briefcase,
  CircleUserRound,
  ClipboardList,
  CodeXml,
  Cog,
  Dumbbell,
  Earth,
  FlaskConical,
  FolderPlus,
  Heart,
  Inbox,
  Landmark,
  Leaf,
  Library,
  Link,
  MessageSquareQuote,
  Mic,
  Paperclip,
  Plane,
  Plus,
  Radical,
  ScanText,
  SquarePen,
  Stethoscope,
  TrendingUp,
  Utensils,
  Table,
} from "lucide-vue-next";

// Public name -> Lucide component map. Names match what the rest of the app
// uses; alias old SF-Symbol-style names so we don't have to refactor every
// caller in this pass.
const ICONS = {
  // navigation / chrome
  chevron: ChevronRight,
  external: ArrowUpRight,
  chevronLeft: ChevronLeft,
  chevronDown: ChevronDown,
  more: MoreHorizontal,
  close: X,

  // people / sharing
  friends: Users,
  userPlus: UserPlus,
  share: Share2,

  // devices
  devices: Monitor,
  laptop: Laptop,
  mac: Laptop,
  monitor: Monitor,
  phone: Smartphone,
  iphone: Smartphone,
  tablet: Tablet,
  ipad: Tablet,
  // The Windows app's devices (iOS SF Symbol `pc`).
  pc: Computer,
  safari: Compass,
  browser: Compass,
  compass: Compass,

  // settings groups
  language: Languages,
  globe: Globe,
  theme: Moon,
  themeLight: Sun,
  themeDark: Moon,
  themeAuto: Settings2,
  bell: Bell,
  storage: HardDrive,
  folder: Folder,
  folderOpen: FolderOpen,
  search: Search,

  // legal / docs
  shield: Shield,
  scroll: ScrollText,
  document: FileText,
  scale: Scale,
  lock: Lock,
  lightbulb: Lightbulb,
  eye: Eye,
  eyeOff: EyeOff,
  pencilLine: PencilLine,
  retry: RotateCcw,

  // actions
  pencil: Pencil,
  copy: Copy,
  trash: Trash2,
  signout: LogOut,
  save: Save,
  undo: Undo2,
  redo: Redo2,
  // iOS-like replacements for the note editor chrome.
  shareBox: Share,
  personAdd: UserRoundPlus,
  textColor: Baseline,
  quoteText: TextQuote,
  codeBlock: CodeXml,
  check: Check,
  print: Printer,
  exportUp: ArrowUpFromLine,
  swap: ArrowLeftRight,
  spinner: Loader2,

  // brand-ish
  diamond: Diamond,
  gem: Gem,
  star: Star,
  sparkles: Sparkles,
  zap: Zap,

  // social
  tiktok: Music,
  instagram: Instagram,

  // support / help
  help: LifeBuoy,
  mail: Mail,
  at: AtSign,

  // media
  image: ImageIcon,
  audio: Volume2,
  cloud: Cloud,
  // What stands in for a title in the notes list (NoteRowWidget symbols).
  cube: Box,
  formula: Sigma,
  scribble: LineSquiggle,

  // dates
  calendar: Calendar,

  // editor formatting
  bold: Bold,
  italic: Italic,
  underline: Underline,
  strikethrough: Strikethrough,
  heading1: Heading1,
  heading2: Heading2,
  heading3: Heading3,
  list: List,
  listOrdered: ListOrdered,
  quote: Quote,
  code: Code,
  palette: Palette,
  highlighter: Highlighter,
  // iOS tablecells
  table: Table,

  // signed-in app: note types (iOS `imageForNoteType`), collections, actions
  noteClassic: FileText,
  noteVideo: Globe,
  noteAudio: Mic,
  noteLesson: BookOpen,
  noteImport: Inbox,
  noteScan: ScanText,
  noteOther: MessageSquareQuote,
  library: Library,
  link: Link,
  plus: Plus,
  newNote: SquarePen,
  folderPlus: FolderPlus,
  profile: CircleUserRound,
  paperclip: Paperclip,
  clipboard: ClipboardList,

  // folder subjects (iOS `FolderSubjectTheme`)
  subjectMath: Radical,
  subjectPhysics: Atom,
  subjectChemistry: FlaskConical,
  subjectBiology: Leaf,
  subjectHistory: Landmark,
  subjectGeography: Earth,
  subjectLanguages: Languages,
  subjectLiterature: BookText,
  subjectComputing: CodeXml,
  subjectEconomics: TrendingUp,
  subjectMusic: Music,
  subjectArt: Palette,
  subjectSport: Dumbbell,
  subjectMedicine: Stethoscope,
  subjectPsychology: Brain,
  brain: Brain,
  subjectEngineering: Cog,
  subjectCooking: Utensils,
  subjectTravel: Plane,
  subjectWork: Briefcase,
  subjectPersonal: Heart,
};

export default {
  name: "SfIcon",
  props: {
    name: { type: String, required: true },
    size: { type: [Number, String], default: 18 },
    strokeWidth: { type: [Number, String], default: 1.75 },
  },
  computed: {
    component() {
      return ICONS[this.name] || null;
    },
  },
};
</script>

<style scoped>
.sf-icon {
  display: block;
  flex-shrink: 0;
}
</style>
